import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Card, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, FileDown, FileText, Printer, RotateCcw, Scissors, Tag, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LotDocumentClient, openPdf, saveDownload, type LotDocumentKind } from "../../api/documents";
import { refusalMessage } from "../../api/manufacturing";
import type { ProductionOrderDTO } from "../../api/production";

/**
 * A lot's paper, in one tab: the travellers, the production sheet and the
 * customer-service labels to print, the cut list for Cut Rite, the drawings a
 * reprint would use, and each truck's manifest.
 *
 * <p>Everything prints from what the lot was released with. A drawing the
 * design office has replaced since is flagged, because the floor must know the
 * paper it holds is no longer the latest.</p>
 */
export function LotDocumentsTab({ order }: { order: ProductionOrderDTO }) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const documents = useMemo(() => new LotDocumentClient(http), [http]);
  const [busy, setBusy] = useState<string | null>(null);
  const released = order.status !== "PLANNED" && order.status !== "CANCELLED";

  const summary = useQuery({
    queryKey: ["production", "documents", order.id],
    queryFn: () => documents.summary(order.id),
    enabled: released,
  });

  const putBack = useMutation({
    mutationFn: () => documents.putBackInOutbox(order.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["production", "documents", order.id] });
      toast.success(t("lot-docs.cutlist.put-back.done", "The cut list is back in Cut Rite's outbox."));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const run = async (key: string, work: () => Promise<void>) => {
    setBusy(key);
    try {
      await work();
    } catch (failure) {
      toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
    } finally {
      setBusy(null);
    }
  };
  const print = (kind: LotDocumentKind) => run(kind, () => openPdf(() => documents.lotDocument(order.id, kind)));

  if (!released) {
    return (
      <p className="text-[13px] text-ink-muted">
        {t("lot-docs.not-released", "The lot's documents are printed once it is released: its components and their steps are written then.")}
      </p>
    );
  }
  if (summary.isLoading) {
    return <p className="text-[13px] text-ink-muted">{t("common.loading", "Loading…")}</p>;
  }
  const data = summary.data;
  if (!data) {
    return <p className="text-[13px] text-risk">{t("lot-docs.load-failed", "The lot's documents could not be read.")}</p>;
  }
  if (data.component_count === 0) {
    return (
      <p className="text-[13px] text-ink-muted">
        {t("lot-docs.no-components", "This lot has no components to print: nothing in its bill is made on the floor.")}
      </p>
    );
  }

  const missing = data.drawings.filter((drawing) => drawing.is_missing);
  const newer = data.drawings.filter((drawing) => drawing.has_newer);

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-[13px] font-semibold">{t("lot-docs.print.title", "Print")}</h3>
            <p className="mt-1 text-[12px] text-ink-muted">
              {t("lot-docs.print.summary", "{{components}} components, {{pages}} drawing pages (one per pallet), {{labels}} service labels.", {
                components: data.component_count,
                pages: data.traveller_pages,
                labels: data.label_count,
              })}
            </p>
            {data.has_painted && (
              <Badge variant="warning" size="sm" className="mt-2">
                {t("lot-docs.painted", "Painted parts (MDF): extra production sheets")}
              </Badge>
            )}
          </div>
          <Button variant="primary" loading={busy === "package"} disabled={busy !== null} onClick={() => void print("package")}>
            <Printer className="size-4" />
            {t("lot-docs.package", "Lot package")}
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="secondary" loading={busy === "travellers"} disabled={busy !== null} onClick={() => void print("travellers")}>
            <FileText className="size-4" />
            {t("lot-docs.travellers", "Drawings")}
          </Button>
          <Button variant="secondary" loading={busy === "sheet"} disabled={busy !== null} onClick={() => void print("sheet")}>
            <FileText className="size-4" />
            {t("lot-docs.sheet", "Production sheet")}
          </Button>
          <Button
            variant="secondary"
            loading={busy === "labels"}
            disabled={busy !== null || data.label_count === 0}
            onClick={() => void print("labels")}
          >
            <Tag className="size-4" />
            {t("lot-docs.labels", "Service labels ({{n}})", { n: data.label_count })}
          </Button>
        </div>
        {data.label_count === 0 && (
          <p className="mt-2 text-[12px] text-ink-muted">
            {t("lot-docs.labels.none", "No customer-service labels: only a product lot that is not a Kanban gets them.")}
          </p>
        )}
      </Card>

      <Card className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-[13px] font-semibold">
              <Scissors className="size-4" />
              {t("lot-docs.cutlist.title", "Cut Rite")}
            </h3>
            <p className="mt-1 text-[12px] text-ink-muted">
              {data.cutlist_fetched_at
                ? t("lot-docs.cutlist.fetched", "Cut Rite took the cut list on {{when}}.", {
                    when: new Date(data.cutlist_fetched_at).toLocaleString(),
                  })
                : t("lot-docs.cutlist.waiting", "Waiting in the outbox: the Cut Rite PC picks it up within a few minutes.")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.cutlist_fetched_at && canWrite && (
              <Button
                variant="ghost"
                loading={putBack.isPending}
                onClick={async () => {
                  if (
                    await confirm({
                      title: t("lot-docs.cutlist.put-back", "Send to Cut Rite again"),
                      message: t(
                        "lot-docs.cutlist.put-back.message",
                        "The cut list goes back in the outbox and is written into Cut Rite's import folder again on the next pick-up.",
                      ),
                      confirmLabel: t("lot-docs.cutlist.put-back", "Send to Cut Rite again"),
                    })
                  ) {
                    putBack.mutate();
                  }
                }}
              >
                <RotateCcw className="size-4" />
                {t("lot-docs.cutlist.put-back", "Send to Cut Rite again")}
              </Button>
            )}
            <Button
              variant="secondary"
              loading={busy === "cutlist"}
              disabled={busy !== null}
              onClick={() => void run("cutlist", () => saveDownload(() => documents.lotCutlist(order.id)))}
            >
              <FileDown className="size-4" />
              {t("lot-docs.cutlist.download", "Download cut list")}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-4 sm:p-5">
        <h3 className="text-[13px] font-semibold">{t("lot-docs.drawings.title", "Drawings")}</h3>
        {missing.length === 0 && newer.length === 0 ? (
          <p className="mt-1 text-[12px] text-ink-muted">
            {t("lot-docs.drawings.ok", "Every component has its drawing, and none has been replaced since release.")}
          </p>
        ) : (
          <ul className="mt-2 flex flex-col gap-1.5">
            {newer.map((drawing) => (
              <li key={`newer-${String(drawing.production_order_component_id)}`} className="flex items-start gap-2 text-[13px]">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" />
                <span>
                  <span className="font-medium">{drawing.code}</span> · {drawing.part_number}:{" "}
                  {drawing.released_part_file_id == null
                    ? t("lot-docs.drawings.added", "a drawing was added after release; the drawings print without it.")
                    : t("lot-docs.drawings.newer", "a newer drawing exists ({{current}}); the drawings print the released one ({{released}}).", {
                        current: drawing.current_revision ?? t("lot-docs.drawings.no-revision", "no revision"),
                        released: drawing.released_revision ?? t("lot-docs.drawings.no-revision", "no revision"),
                      })}
                </span>
              </li>
            ))}
            {missing.map((drawing) => (
              <li key={`missing-${String(drawing.production_order_component_id)}`} className="flex items-start gap-2 text-[13px]">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-ink-faint" />
                <span>
                  <span className="font-medium">{drawing.code}</span> · {drawing.part_number}:{" "}
                  {t("lot-docs.drawings.missing", "no drawing; its drawing page says so in place of the picture.")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-4 sm:p-5">
        <h3 className="flex items-center gap-2 text-[13px] font-semibold">
          <Truck className="size-4" />
          {t("lot-docs.transfers.title", "Transfer manifests")}
        </h3>
        {data.transfers.length === 0 ? (
          <p className="mt-1 text-[12px] text-ink-muted">{t("lot-docs.transfers.none", "Nothing has left for Terrebonne yet.")}</p>
        ) : (
          <ul className="mt-2 flex flex-col divide-y divide-line">
            {data.transfers.map((transfer) => (
              <li key={transfer.id} className="flex flex-wrap items-center gap-3 py-2 text-[13px]">
                <span className="font-medium">{t("lot-docs.transfers.number", "Transfer {{n}}", { n: transfer.number })}</span>
                <span className="text-ink-muted">{transfer.shipped_date}</span>
                {transfer.truck_reference && <span className="text-ink-muted">{transfer.truck_reference}</span>}
                <Badge variant={transfer.status === "RECEIVED" ? "success" : transfer.status === "CANCELLED" ? "neutral" : "info"} size="sm">
                  {transfer.status === "RECEIVED"
                    ? t("lot-docs.transfers.received", "Received")
                    : transfer.status === "CANCELLED"
                      ? t("lot-docs.transfers.cancelled", "Cancelled")
                      : t("lot-docs.transfers.shipped", "On its way")}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  loading={busy === `transfer-${String(transfer.id)}`}
                  disabled={busy !== null}
                  onClick={() =>
                    void run(`transfer-${String(transfer.id)}`, () => openPdf(() => documents.transferManifest(transfer.id)))
                  }
                >
                  <Printer className="size-4" />
                  {t("lot-docs.transfers.manifest", "Manifest")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
